import { useEffect, useRef, useState } from "react";
import { Send, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

export default function ChatTest() {
  const [sid] = useState(() => `test_${Date.now()}`);
  const [msgs, setMsgs] = useState([{ role: "assistant", message: "Hi! I'm the Megh Balika AI Assistant. Try asking me anything — in English or Bengali/Banglish (e.g., 'ki khabo?', 'kal ki available?', 'senior discount ache?')" }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!input.trim() || busy) return;
    const userMsg = input;
    setMsgs(m => [...m, { role: "user", message: userMsg }]);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/chat/message", { session_id: sid, message: userMsg });
      setMsgs(m => [...m, { role: "assistant", message: r.data.reply, lang: r.data.lang }]);
    } catch {
      setMsgs(m => [...m, { role: "assistant", message: "Sorry, connection issue." }]);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-serif text-4xl">WhatsApp AI Assistant · Test</h1>
        <p className="text-[#8A7970] mt-1">This is exactly how your WhatsApp bot will reply to guests. Bengali/Banglish → Bengali reply. English → English reply.</p>
      </div>

      <div className="max-w-3xl bg-[#FDFBF7] rounded-2xl border border-[#E7C2A6]/40 flex flex-col" style={{height: "70vh"}}>
        <div className="flex items-center gap-3 p-4 border-b border-[#E7C2A6]/40 bg-[#25D366]/10 rounded-t-2xl">
          <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center text-white"><Bot className="w-5 h-5" strokeWidth={1.5}/></div>
          <div>
            <div className="font-medium">Megh Balika WhatsApp Bot</div>
            <div className="text-xs text-[#8A7970]">Bilingual · Powered by AI</div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl whitespace-pre-wrap text-sm ${m.role === "user" ? "bg-[#B84F3E] text-white rounded-br-sm" : "bg-white border border-[#E7C2A6]/50 rounded-bl-sm"}`}>
                {m.message}
                {m.lang && <div className="text-[10px] mt-1 opacity-60">lang: {m.lang}</div>}
              </div>
            </div>
          ))}
          {busy && <div className="flex justify-start"><div className="bg-white border border-[#E7C2A6]/50 px-4 py-2.5 rounded-2xl text-sm">...</div></div>}
          <div ref={endRef}/>
        </div>

        <div className="p-4 border-t border-[#E7C2A6]/40 flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type in English or Bengali/Banglish..." data-testid="chat-input" disabled={busy}/>
          <Button onClick={send} disabled={busy} className="bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="chat-send-btn"><Send className="w-4 h-4" strokeWidth={1.5}/></Button>
        </div>
      </div>

      <div className="mt-6 max-w-3xl bg-[#F5F2EB] rounded-xl p-5 text-sm text-[#4A403B] border border-[#E7C2A6]/40">
        <div className="font-medium mb-2">Sample prompts to try:</div>
        <ul className="space-y-1 text-[#8A7970] text-xs">
          <li>• "Hi, I want to book for 3 nights from Feb 15. 2 adults + 1 kid (5 years). What packages?"</li>
          <li>• "kal available ache? 2 jon adult. saver package ki?"</li>
          <li>• "How far is Sonajhuri Haat? Any pickup from station?"</li>
          <li>• "amar 65 bochor bayosh, weekday te discount ache?"</li>
          <li>• "Is food veg or non-veg? Any advance needed?"</li>
        </ul>
      </div>
    </div>
  );
}
